import { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { X } from "lucide-react"
import Tooltip from "@mui/material/Tooltip"
import { Button, Textarea, Currency, StatusBadge } from "../../ui"
import { useGetProposalComparisonQuery, useSaveProposalComparisonMutation } from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import LinkActions, { proposalLink } from "./LinkActions"
import ConflictOfInterestModal from "./ConflictOfInterestModal"
import { hasQuote, quotedItems, proposalTotal, proposalLineKey, formatWarranty } from "../../constants"
import { COMPARISON_SCOPE_TYPE, buildCriteriaRows, criterionOk, defaultValue, hasValue, formatScoring } from "./proposalScoring"

const itemFor = (proposal, lineKey) => (proposal.items || []).find((it) => proposalLineKey(it) === lineKey)

const buildItemRows = (quoted) => {
  const rows = []
  const seen = new Set()
  quoted.forEach((p) =>
    quotedItems(p).forEach((it) => {
      const key = proposalLineKey(it)
      if (seen.has(key)) return
      seen.add(key)
      rows.push({ key, itemName: it.itemName })
    })
  )
  return rows
}

const cellStyle = {
  padding: "10px 14px",
  borderBottom: "1px solid var(--pc-border)",
  textAlign: "left",
  verticalAlign: "top",
  overflowWrap: "anywhere",
}
const headStyle = { ...cellStyle, background: "var(--pc-surface-2)", fontWeight: 700 }
const ITEM_COL_WIDTH = 200
const VENDOR_COL_WIDTH = 200
const clamp3 = { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }

const sameSet = (a, b) => a.size === b.size && [...a].every((x) => b.has(x))

// "Review and compare quotation": vendors (columns) × quoted items + fixed and
// per-OR/PO requirement scoring criteria (rows). Ticks are seeded from the last
// saved round (`tickedCriteria`), or auto-ticked from filled-in values when the
// proposal was never scored.
// Save (POST /proposal-comparisons) sends winner + preferred + scoring + note
// in one transaction and is enabled by any change, not just a new winner.
export default function VendorComparisonModal({ open, onClose, proposals, sourceType = "order", sourceId, onSelected }) {
  const scopeType = COMPARISON_SCOPE_TYPE[sourceType] || COMPARISON_SCOPE_TYPE.order
  const { data: comparison } = useGetProposalComparisonQuery(
    { scopeType, scopeId: sourceId },
    { skip: !open || !sourceId }
  )
  const [saveComparison, { isLoading: selecting }] = useSaveProposalComparisonMutation()
  const authUser = useSelector((state) => state.auth.user)

  const allProposals = comparison?.proposals || proposals || []
  const quoted = allProposals.filter(hasQuote)
  const itemRows = buildItemRows(quoted)
  const ctx = { lineKeys: itemRows.map((r) => r.key) }
  const criteriaRows = buildCriteriaRows(comparison?.criteria)
  const winnerId = allProposals.find((p) => p.isSelected)?.id ?? null
  const notes = comparison?.comparisonNotes || []

  const [selectedId, setSelectedId] = useState(null)
  const [pendingChoice, setPendingChoice] = useState(null)
  const [reason, setReason] = useState("")
  // { [vendorProposalItemId]: bool } — only items the user flipped.
  const [preferredOverride, setPreferredOverride] = useState({})
  const [ticks, setTicks] = useState({})
  const [seedTicks, setSeedTicks] = useState({})
  const seededRef = useRef(false)

  // Seed once per open, after the GET lands; refetches while open must not
  // wipe what the user already ticked.
  useEffect(() => {
    if (!open) {
      seededRef.current = false
      return
    }
    if (seededRef.current || !comparison) return
    seededRef.current = true
    const seedQuoted = comparison.proposals.filter(hasQuote)
    const seedCtx = { lineKeys: buildItemRows(seedQuoted).map((r) => r.key) }
    const seedRows = buildCriteriaRows(comparison.criteria)
    const next = {}
    seedQuoted.forEach((p) => {
      const saved = p.tickedCriteria && new Set(p.tickedCriteria)
      next[p.id] = new Set(
        seedRows.filter((r) => (saved ? saved.has(r.key) : criterionOk(r, p, seedCtx))).map((r) => r.key)
      )
    })
    setTicks(next)
    setSeedTicks(next)
    setSelectedId(comparison.proposals.find((p) => p.isSelected)?.id ?? null)
  }, [open, comparison])

  if (!open) return null

  const isPreferred = (it) => preferredOverride[it.id] ?? !!it.isPreferred
  const togglePreferred = (it) => setPreferredOverride((prev) => ({ ...prev, [it.id]: !isPreferred(it) }))
  const ticked = (p) => ticks[p.id] || new Set()
  const toggleTick = (p, key) =>
    setTicks((prev) => {
      const next = new Set(prev[p.id] || [])
      next.has(key) ? next.delete(key) : next.add(key)
      return { ...prev, [p.id]: next }
    })
  const topScore = Math.max(0, ...quoted.map((p) => ticked(p).size))
  const isSuggested = (p) => topScore > 0 && ticked(p).size === topScore
  const vendorName = (p) => p.supplierName || p.supplier?.name

  const quoteLines = quoted.flatMap((p) => quotedItems(p).filter((it) => it.id != null))
  const preferredChanged = quoteLines.some((it) => isPreferred(it) !== !!it.isPreferred)
  const ticksChanged = quoted.some((p) => !sameSet(ticked(p), seedTicks[p.id] || new Set()))
  const winnerChanged = selectedId != null && selectedId !== winnerId
  const dirty = preferredChanged || ticksChanged || winnerChanged || !!reason.trim()
  const canSave = !!comparison && dirty && (!winnerChanged || reason.trim()) && !selecting

  const handleClose = () => {
    if (selecting) return
    setSelectedId(null)
    setReason("")
    setPreferredOverride({})
    setTicks({})
    setSeedTicks({})
    onClose()
  }

  const handleSave = async () => {
    if (!canSave) return
    const chosen = allProposals.find((p) => p.id === (selectedId ?? winnerId))
    const vendors = quoted.map((p) => ({ proposalId: p.id, supplierName: vendorName(p), score: ticked(p).size, ticked: [...ticked(p)] }))
    const noteText = formatScoring({
      by: authUser?.email,
      rows: criteriaRows,
      vendors: quoted.map((p) => ({
        id: p.id,
        name: vendorName(p),
        ticked: ticked(p),
        preferred: quotedItems(p).some((it) => it.id != null && isPreferred(it)),
        lines: quotedItems(p).map((it) => ({ name: it.itemName, amount: it.proposedAmount })),
      })),
      chosen: chosen ? { name: vendorName(chosen), reason: reason.trim() } : null,
    })
    try {
      await saveComparison({
        scopeType,
        scopeId: sourceId,
        selectedProposalId: winnerChanged ? selectedId : null,
        reason: reason.trim(),
        preferred: preferredChanged ? quoteLines.map((it) => ({ vendorProposalItemId: it.id, preferred: isPreferred(it) })) : [],
        scoring: { maxScore: criteriaRows.length, vendors },
        noteText,
      }).unwrap()
      showToastSuccess(
        winnerChanged && sourceType === "purchase"
          ? "Vendor selected — the PO's total will update shortly."
          : winnerChanged
          ? "Vendor selected."
          : "Comparison saved."
      )
      handleClose()
      onSelected?.()
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Couldn't save this comparison. Please try again."))
    }
  }

  return (
    <>
      <div
        onClick={handleClose}
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.5)", zIndex: 1300 }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "min(920px, 94vw)",
          maxHeight: "88vh",
          background: "var(--pc-surface)",
          borderRadius: "var(--pc-r-lg)",
          boxShadow: "0 24px 64px rgba(15,23,42,.24)",
          zIndex: 1301,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
            padding: "18px 24px",
            borderBottom: "1px solid var(--pc-border)",
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pc-ink)" }}>
              Review and compare quotations
            </div>
            <div style={{ fontSize: 13, color: "var(--pc-muted)", marginTop: 2 }}>
              Compare each vendor&apos;s pricing side by side, then pick one and record why.
              {sourceType === "purchase" && " Selecting a new winner recalculates the PO's total and budget."}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--pc-muted)", padding: 4 }}
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ padding: "20px 24px", overflowY: "auto", flex: 1 }}>
          {quoted.length === 0 ? (
            <p className="pc-muted" style={{ fontSize: 14 }}>
              No vendor has submitted a quotation yet.
            </p>
          ) : (
            <>
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    minWidth: ITEM_COL_WIDTH + quoted.length * VENDOR_COL_WIDTH,
                    tableLayout: "fixed",
                    borderCollapse: "collapse",
                    fontSize: 13.5,
                  }}
                >
                  <colgroup>
                    <col style={{ width: ITEM_COL_WIDTH }} />
                    {quoted.map((p) => (
                      <col key={p.id} style={{ width: VENDOR_COL_WIDTH }} />
                    ))}
                  </colgroup>
                  <thead>
                    <tr>
                      <th style={headStyle}>Item</th>
                      {quoted.map((p) => (
                        <th key={p.id} style={headStyle}>
                          <Tooltip title={vendorName(p)} arrow placement="top">
                            <div style={clamp3}>{vendorName(p)}</div>
                          </Tooltip>
                          {p.isSelected && (
                            <div style={{ marginTop: 4 }}>
                              <StatusBadge status="approved" label="Selected" />
                            </div>
                          )}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {itemRows.map((row) => (
                      <tr key={row.key}>
                        <td style={cellStyle}>{row.itemName}</td>
                        {quoted.map((p) => {
                          const it = itemFor(p, row.key)
                          return (
                            <td key={p.id} style={cellStyle}>
                              {it?.proposedAmount != null ? <Currency value={it.proposedAmount} align="left" /> : "—"}
                              {formatWarranty(it) && (
                                <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 2 }}>{formatWarranty(it)}</div>
                              )}
                              {it?.id != null && it.proposedAmount != null && (
                                <label
                                  style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 4, fontSize: 12.5, cursor: "pointer" }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isPreferred(it)}
                                    onChange={() => togglePreferred(it)}
                                  />
                                  Preferred
                                </label>
                              )}
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...cellStyle, fontWeight: 700 }}>Total</td>
                      {quoted.map((p) => (
                        <td key={p.id} style={{ ...cellStyle, fontWeight: 700 }}>
                          <Currency value={proposalTotal(p)} align="left" />
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td colSpan={quoted.length + 1} style={{ ...headStyle, fontSize: 12, textTransform: "uppercase" }}>
                        Scoring criteria — tick what each vendor meets
                      </td>
                    </tr>
                    {criteriaRows.map((row) => (
                      <tr key={row.key}>
                        <td style={cellStyle}>
                          {row.label}
                          {row.key === "deliveryFee" && (
                            <div style={{ fontSize: 12, color: "var(--pc-muted)" }}>not included in total</div>
                          )}
                        </td>
                        {quoted.map((p) => {
                          const v = row.get(p, ctx)
                          const fallback = defaultValue(row, p)
                          return (
                            <td key={p.id} style={cellStyle}>
                              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                                <input
                                  type="checkbox"
                                  checked={ticked(p).has(row.key)}
                                  onChange={() => toggleTick(p, row.key)}
                                  style={{ marginTop: 2 }}
                                />
                                {hasValue(v) ? (
                                  <span>{v}</span>
                                ) : hasValue(fallback) ? (
                                  <span style={{ color: "var(--pc-muted)" }}>default: {fallback}</span>
                                ) : (
                                  <span>-</span>
                                )}
                              </label>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...cellStyle, fontWeight: 700 }}>Score</td>
                      {quoted.map((p) => (
                        <td key={p.id} style={{ ...cellStyle, fontWeight: 700 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            {ticked(p).size}/{criteriaRows.length}
                            {isSuggested(p) && <StatusBadge status="info" label="Suggested" />}
                          </div>
                        </td>
                      ))}
                    </tr>
                    {quoted.some((p) => proposalLink(p)) && (
                      <tr>
                        <td style={cellStyle}>Link</td>
                        {quoted.map((p) => (
                          <td key={p.id} style={cellStyle}>
                            <LinkActions link={proposalLink(p)} />
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr>
                      <td style={cellStyle}>Choose this vendor</td>
                      {quoted.map((p) => (
                        <td key={p.id} style={cellStyle}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                            {/* Exclusive and never empty: the API can't unset a winner. */}
                            <input
                              type="checkbox"
                              checked={selectedId === p.id}
                              onChange={() => selectedId !== p.id && setPendingChoice(p)}
                            />
                            Select
                            {isSuggested(p) && (
                              <span style={{ fontSize: 12, color: "var(--pc-muted)" }}>(highest score)</span>
                            )}
                          </label>
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 18, maxWidth: 480 }}>
                <Textarea
                  label="Reason for this choice"
                  required={winnerChanged}
                  placeholder="e.g. Lowest total price with the fastest delivery timeline"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
                <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 6 }}>
                  Your scores, preferred picks and reason are saved as a note everyone on this request can read.
                </div>
              </div>
              {notes.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Scoring history</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {notes.map((n) => (
                      <pre
                        key={n.id}
                        style={{
                          margin: 0,
                          padding: "10px 12px",
                          background: "var(--pc-surface-2)",
                          borderRadius: "var(--pc-r-md)",
                          fontFamily: "inherit",
                          fontSize: 12.5,
                          whiteSpace: "pre-wrap",
                          color: "var(--pc-body)",
                        }}
                      >
                        {n.noteText}
                      </pre>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid var(--pc-border)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
          }}
        >
          <Button variant="secondary" onClick={handleClose} disabled={selecting}>
            Cancel
          </Button>
          {quoted.length > 0 && (
            <Button variant="primary" onClick={handleSave} disabled={!canSave}>
              {selecting ? "Saving…" : "Save"}
            </Button>
          )}
        </div>
      </div>
      {pendingChoice && (
        <ConflictOfInterestModal
          vendorName={vendorName(pendingChoice)}
          initialNote={reason}
          onCancel={() => setPendingChoice(null)}
          onConfirm={(note) => {
            if (note != null) setReason(note)
            setSelectedId(pendingChoice.id)
            setPendingChoice(null)
          }}
        />
      )}
    </>
  )
}
